/**
 * ListPager — the one pager every server-paged list draws (business lists, the
 * invoice history on both web portals).
 *
 * The lists page on the SERVER (each request asks for one page and gets the
 * total back), so this renders only the controls: previous, "Page X of Y",
 * next. It renders nothing for a single page. The page and page count are also
 * written as data attributes, so a test reads them as numbers; the buttons are
 * `<testId>-prev` / `<testId>-next`.
 */
import { useTranslation } from '@tonnie/locale';

import { Button } from '../primitives/button';

export interface ListPagerProps {
  page: number;
  pages: number;
  onPage: (page: number) => void;
  /** Names the pager for tests (`data-testid`). */
  testId: string;
  /** A page is loading — both buttons wait for it. */
  busy?: boolean;
}

export function ListPager({ page, pages, onPage, testId, busy = false }: ListPagerProps) {
  const { t } = useTranslation();
  if (pages <= 1) return null;
  return (
    <nav
      aria-label={t('common.pagination')}
      className="flex items-center justify-center gap-3 pt-4"
      data-testid={testId}
      data-page={page}
      data-pages={pages}
    >
      <Button
        variant="outline"
        size="sm"
        data-testid={`${testId}-prev`}
        disabled={page <= 1 || busy}
        onClick={() => onPage(Math.max(1, page - 1))}
      >
        {t('common.previous')}
      </Button>
      <span className="text-sm text-muted-foreground" aria-live="polite">
        {t('common.pageOf', { page, total: pages })}
      </span>
      <Button
        variant="outline"
        size="sm"
        data-testid={`${testId}-next`}
        disabled={page >= pages || busy}
        onClick={() => onPage(Math.min(pages, page + 1))}
      >
        {t('common.next')}
      </Button>
    </nav>
  );
}
